import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import useSound from 'use-sound';
import ScanInput from './ScanInput';
import OrderDisplay from './OrderDisplay';
import BoxingStation from './BoxingStation';
import ShippingDocuments from './ShippingDocuments';
import './FulfillmentScanner.css';

function FulfillmentScanner() {
  const [mode, setMode] = useState('order'); // 'order', 'items', 'boxing', 'documents', 'complete'
  const [salesOrder, setSalesOrder] = useState(null);
  const [scannedItems, setScannedItems] = useState([]);
  const [fulfillmentData, setFulfillmentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('Scan Sales Order to begin');

  // Sound effects
  const [playSuccess] = useSound('/sounds/success.mp3', { volume: 0.5 });
  const [playError] = useSound('/sounds/error.mp3', { volume: 0.5 });
  const [playComplete] = useSound('/sounds/complete.mp3', { volume: 0.7 });

  // Auto-focus management
  const scanInputRef = useRef(null);

  useEffect(() => {
    // Keep scan input focused
    if (scanInputRef.current) {
      scanInputRef.current.focus();
    }
  }, [mode, salesOrder]);

  // Handle sales order scan
  const handleOrderScan = async (orderNumber) => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`/api/fulfillment/order/${orderNumber}`);

      if (response.data) {
        setSalesOrder(response.data);
        setMode('items');
        setMessage(`Order ${orderNumber} loaded. Start scanning items.`);
        playSuccess();
      }
    } catch (err) {
      setError(`Order not found: ${orderNumber}`);
      playError();
      console.error('Failed to load order:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle item scan
  const handleItemScan = async (itemSku) => {
    if (!salesOrder) return;

    // Check for undo command
    if (itemSku.toLowerCase() === 'undo' || itemSku === '999999') {
      handleUndo();
      return;
    }

    try {
      // Verify item is in the order
      const orderItem = salesOrder.items.find(item =>
        item.sku === itemSku || item.upc === itemSku
      );

      if (!orderItem) {
        setError(`Item ${itemSku} not in this order`);
        playError();
        return;
      }

      // Check if already fully scanned
      const scannedQty = scannedItems.filter(item => item.sku === orderItem.sku).length;
      if (scannedQty >= orderItem.quantity) {
        setError(`Item ${itemSku} already fully scanned`);
        playError();
        return;
      }

      // Add to scanned items
      const scannedItem = {
        ...orderItem,
        timestamp: new Date().toISOString()
      };

      setScannedItems([...scannedItems, scannedItem]);

      playSuccess();
      setMessage(`Scanned: ${orderItem.description}`);
      setError(null);

      // Check if all items scanned
      const totalScanned = scannedItems.length + 1;
      const totalRequired = salesOrder.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);

      if (totalScanned === totalRequired) {
        playComplete();
        setMessage('All items scanned! Proceed to boxing.');
      }
    } catch (err) {
      setError('Failed to process item scan');
      playError();
      console.error('Item scan error:', err);
    }
  };

  // Handle undo last scan
  const handleUndo = () => {
    if (scannedItems.length === 0) {
      setError('No items to undo');
      playError();
      return;
    }

    // Remove last scanned item
    const lastItem = scannedItems[scannedItems.length - 1];
    const newScannedItems = scannedItems.slice(0, -1);
    setScannedItems(newScannedItems);

    playSuccess();
    setMessage(`Removed: ${lastItem.description}`);
    setError(null);
  };

  // Proceed to boxing station
  const handleProceedToBoxing = () => {
    setMode('boxing');
    setMessage('Proceed to boxing station');
  };

  // Handle going back from boxing to scanning
  const handleBackToScanning = () => {
    setMode('items');
    setMessage('Continue scanning items');
  };

  // Handle fulfillment completion from boxing station
  const handleFulfillmentComplete = (result) => {
    if (result.showDocuments) {
      setFulfillmentData(result);
      setMode('documents');
      setMessage('Proceed with shipping documents');
    } else {
      playComplete();
      setMessage(`Fulfillment ${result.fulfillmentId} created successfully!`);
      setMode('complete');

      // Reset for next order after delay
      setTimeout(() => {
        resetOrder();
      }, 5000);
    }
  };

  // Handle completion from documents page
  const handleDocumentsComplete = () => {
    playComplete();
    setMessage('Order completed successfully!');
    resetOrder();
  };

  // Reset for next order
  const resetOrder = () => {
    setSalesOrder(null);
    setScannedItems([]);
    setFulfillmentData(null);
    setMode('order');
    setMessage('Scan Sales Order to begin');
  };

  // Main scan handler
  const handleScan = (value) => {
    if (mode === 'order') {
      handleOrderScan(value);
    } else if (mode === 'items') {
      handleItemScan(value);
    }
  };

  // Show boxing station for boxing mode
  if (mode === 'boxing') {
    return (
      <BoxingStation
        salesOrder={salesOrder}
        scannedItems={scannedItems}
        onComplete={handleFulfillmentComplete}
        onBack={handleBackToScanning}
      />
    );
  }

  // Show shipping documents for documents mode
  if (mode === 'documents' && fulfillmentData) {
    return (
      <ShippingDocuments
        salesOrder={salesOrder}
        scannedItems={scannedItems}
        boxes={fulfillmentData.boxes}
        fulfillmentId={fulfillmentData.fulfillmentId}
        onComplete={handleDocumentsComplete}
      />
    );
  }

  return (
    <div className="fulfillment-scanner">
      <AnimatePresence mode="wait">
        <motion.div
          key={mode}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="scanner-main"
        >
          {/* Message Display */}
          <div className={`scanner-message ${error ? 'error' : 'success'}`}>
            {error || message}
          </div>

          {/* Scan Input with Undo Button */}
          <div style={{ position: 'relative' }}>
            <ScanInput
              ref={scanInputRef}
              onScan={handleScan}
              placeholder={mode === 'order' ? 'Scan Sales Order...' : 'Scan Item SKU...'}
              disabled={loading || mode === 'complete'}
            />
            {mode === 'items' && scannedItems.length > 0 && (
              <button
                onClick={handleUndo}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: '#ff9800',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Undo Last (or scan "UNDO")
              </button>
            )}
          </div>

          {/* Order Display */}
          {salesOrder && (
            <OrderDisplay
              order={salesOrder}
              scannedItems={scannedItems}
              mode={mode}
            />
          )}

          {/* Action Buttons for Items Mode */}
          {mode === 'items' && salesOrder && (
            <div className="scanning-actions">
              <div className="progress-info">
                <span className="scanned-count">
                  {scannedItems.length} / {salesOrder.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0)} items scanned
                </span>
              </div>

              <div className="action-buttons">
                <button
                  className="incomplete-button"
                  onClick={handleProceedToBoxing}
                  disabled={scannedItems.length === 0}
                >
                  Fulfill Incomplete & Box
                </button>

                {scannedItems.length === salesOrder.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0) && (
                  <button
                    className="proceed-button"
                    onClick={handleProceedToBoxing}
                  >
                    All Items Scanned - Proceed to Boxing
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Complete Message */}
          {mode === 'complete' && (
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              className="complete-section"
            >
              <div className="success-icon">✅</div>
              <h2>Fulfillment Complete!</h2>
              <p>Order has been successfully fulfilled and shipped.</p>
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Loading Overlay */}
      {loading && (
        <div className="loading-overlay">
          <div className="loader">Processing...</div>
        </div>
      )}
    </div>
  );
}

export default FulfillmentScanner;